// Tunable thresholds — Detection Engine System 1.
//
// ВСЕ числовые [T]-пороги собраны здесь, в одном месте, чтобы калибровать на
// новом geometry-first датасете (НЕ bench-29 — он single-axis, spec §6).
//
// Две категории:
//   FROZEN — пороги Section B / remark_s201 (s173, НЕ трогать без D&R-решения).
//   TUNABLE [T] — относительные доли, level_band, ε — провизорны, под тюнинг.

export const THRESHOLDS = {
  // ── FROZEN (Section B, s173) ───────────────────────────────────────────
  // Расстояние «параллельно» / «singleton-разрыв» — деления шкалы −3..+3.
  PARALLEL_MAX_GAP: 1, // parallel-whole: |Lₐ−L_b| ≤ 1 ∀ stage
  DIVERGENCE_MIN_GAP: 2, // divergence/late-conv «далеко» порог
  SINGLETON_GAP: 2, // |gap(L)| ≥ 2 = выломилась
  CYCLING_MIN_AMP: 2, // cycling амплитуда ≥ 2
  DRIFT_RISE: 2, // drifting: gap_N − gap_1 ≥ 2
  VSHAPE_DROP: 2, // v-shape провал ≥ 2 ниже min(start,end)
  COLLAPSE_HOLD_MIN: 2, // last-/first-to-collapse: K ≥ 2 этапов держания
  COLLAPSE_END_TOL: 1, // last-to-collapse: L(N) ≤ mean(other)+1
  FIELD_LEVEL_MIN_LAYERS: 4, // field-level-recovery: v-shape на ≥ 4 слоях

  // ── TUNABLE [T] ────────────────────────────────────────────────────────
  // Step-ε (§2.2) = NOISE-FLOOR классификации шагов up/down/flat (ратифицирован
  // s443.d5 Фаза 2): дельта меньше ε читается как «плоско», не как движение. Это и
  // есть механизм устойчивости к float-слайдерам — де-snapped микро-колебание
  // (1.4→1.42) поглощается как flat, а не фабрикует up/down. Carrier-type-dependent:
  //   одна реальная линия = целочисленные слайдеры → ε=1;
  //   дробная несущая (group mean / силуэт)       → ε=0.5 (иначе плавный triplet-mean
  //   даёт все flat и step-классы ломаются).
  EPS_INTEGER: 1, // [T] для single real line — noise-floor шага
  EPS_FRACTIONAL: 0.5, // [T] для дробной несущей — noise-floor шага
  // Float-equality guard (§3.3.1): чистый порог «значение точно на поле» для
  // sign-тестов над вещественной арифметикой — ловит −0 и floating-dust от деления
  // среднего, НЕ двигает ни один содержательный порог. Реальное (пусть малое)
  // отклонение остаётся отклонением; его сила и так near-zero.
  EPS_NOISE: 1e-9,

  // Амплитуда «есть движение» (§2.4): A ≥ 2 нужен движущемуся классу.
  MOVEMENT_MIN_AMP: 2, // [T]
  PLATEAU_MAX_AMP: 1, // [T] A ≤ 1 ⇒ plateau

  // Монотонные классы (§2.3 #10/#11).
  MONOTONIC_MIN_NET: 2, // [T] |net| ≥ 2

  // §2.8 forward-проекция Shape II — «риторика, не прогноз» (s384), редакция F4 (s980):
  // конец разворота E = min(+3, max(FLOOR, пик силуэта) + LIFT). Пол абсолютный, по шкале
  // (тем, кто «никогда особо и не жил хорошо», разворот не занижается); LIFT — выход выше
  // лучшего этапа прошлого (эталон CD на Бене: пик 1.57 → E 1.92). Прежний относительный
  // SHAPE_II_MIN_RISE (подъём от последнего этапа) снят вместе с F3.
  SHAPE_II_FLOOR: 1.5, // [T]
  SHAPE_II_LIFT: 0.35, // [T]

  // V-shape carrier-geometry (§2.3 #4) — net-толеранс на концах.
  VSHAPE_NET_TOL: 1, // [T] |c_N − c₁| ≤ 1

  // level_band границы (§2.5) — на фикс −3..+3 шкале (0 = sea level).
  LEVEL_HIGH: 1.5, // [T] high ≥ +1.5
  LEVEL_LOW: -1.5, // [T] low ≤ −1.5

  // v-shape ↔ current-singleton guard (§3.2 rule 2).
  VSHAPE_GUARD: 1.5, // [T] v-shape выигрывает если |L(N)−mean(other-6)| ≤ 1.5

  // wanting CS-below routing (§3.2 rule 4) — peak порог «вакуум vs подавлено».
  WANTING_VACUUM_PEAK: 2, // [T] peak ≥ +2 → вакуум, иначе подавлено

  // Coverage (§3.6).
  CARDS_MIN: 1, // пусто → F.7 scatter
  CARDS_MAX: 3, // потолок реальных карточек
};

// Относительные доли длины (§2.2) — функции от N (число этапов).
// EARLY = первые ⌈(N−1)/3⌉ переходов · LATE = последние max(1, ⌊(N−1)/3⌋)
// MAJORITY_SPAN = ⌈0.6·(N−1)⌉ переходов · HOLD_MIN = ⌈N/2⌉ этапов.
export const SPANS = {
  early: (N) => Math.ceil((N - 1) / 3),
  late: (N) => Math.max(1, Math.floor((N - 1) / 3)),
  majoritySpan: (N) => Math.ceil(0.6 * (N - 1)),
  holdMin: (N) => Math.ceil(N / 2),
};
